// Sync service for offline/online data synchronization
import { API_ROUTES, SYNC_CONFIG } from '@shared/constants';
import { SyncStatus, Task } from '@shared/types';
import { TasksAPI } from './api';
import { AudioRecordingsDB, PhotosDB, SyncQueueDB, TasksDB, db } from './db';

// Helper function for making API requests
async function makeRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: { message: string } }> {
  const apiBaseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3001';
  
  // Get auth token from localStorage
  const authToken = localStorage.getItem('auth_token');
  
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Only set Content-Type if not FormData (FormData sets its own boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const requestOptions: RequestInit = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(`${apiBaseUrl}${url}`, requestOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API request failed:', error);
    return {
      success: false,
      error: {
        message: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
}

let syncInterval: NodeJS.Timeout | null = null;
let isOnline = navigator.onLine;

// Network status monitoring
window.addEventListener('online', () => {
  isOnline = true;
  console.log('Network: Online');
  // Trigger immediate sync when coming back online
  performSync();
});

window.addEventListener('offline', () => {
  isOnline = false;
  console.log('Network: Offline');
});

/**
 * Initialize sync service
 */
export async function initializeSync(): Promise<void> {
  console.log('Initializing sync service...');
  
  try {
    // Start periodic sync
    startPeriodicSync();
    
    // Listen for online/offline events
    window.addEventListener('app:online', handleOnline);
    window.addEventListener('app:offline', handleOffline);
    
    // Initial sync if online (but don't fail if it doesn't work)
    // Note: We don't call health check since that endpoint doesn't exist
    if (navigator.onLine) {
      setTimeout(() => {
        syncData().catch(error => {
          console.warn('Initial sync failed (this is normal if no tasks exist yet):', error);
          // Don't throw error, just log it
        });
      }, 2000); // Increased delay to allow auth token to be set
    }
  } catch (error) {
    console.warn('Sync service initialization failed:', error);
    // Don't throw error, just log it so app can continue
  }
}

/**
 * Start periodic sync
 */
export function startPeriodicSync(): void {
  if (syncInterval) {
    clearInterval(syncInterval);
  }

  // Perform initial sync (but handle errors gracefully)
  performSync().catch(error => {
    console.warn('Initial sync failed:', error);
  });

  // Set up periodic sync
  syncInterval = setInterval(() => {
    if (isOnline) {
      performSync().catch(error => {
        console.warn('Periodic sync failed:', error);
      });
    }
  }, SYNC_CONFIG.INTERVAL);
}

/**
 * Handle online event
 */
async function handleOnline(): Promise<void> {
  console.log('Device is online - starting sync');
  await syncData();
}

/**
 * Handle offline event
 */
function handleOffline(): void {
  console.log('Device is offline - sync paused');
}

/**
 * Main sync function
 */
export async function syncData(): Promise<{
  success: boolean;
  syncedCount: number;
  errors: string[];
}> {
  if (!isOnline) {
    console.log('Sync skipped: offline');
    return { success: false, syncedCount: 0, errors: ['Device is offline'] };
  }

  console.log('Starting sync...');

  const errors: string[] = [];
  let syncedCount = 0;

  try {
    // Sync tasks
    try {
      const taskResult = await syncTasks();
      syncedCount += taskResult.syncedCount;
      errors.push(...taskResult.errors);
    } catch (error) {
      errors.push(`Task sync failed: ${error}`);
    }

    // Sync photos
    try {
      await syncPhotos();
    } catch (error) {
      errors.push(`Photo sync failed: ${error}`);
    }

    // Sync audio recordings
    try {
      await syncAudioRecordings();
    } catch (error) {
      errors.push(`Audio sync failed: ${error}`);
    }

    console.log(`Sync completed: ${syncedCount} items synced, ${errors.length} errors`);
    
    // Emit sync event
    window.dispatchEvent(new CustomEvent('sync:completed', {
      detail: { syncedCount, errors }
    }));

    return {
      success: errors.length === 0,
      syncedCount,
      errors
    };

  } catch (error) {
    console.error('Sync failed:', error);
    errors.push(`Sync failed: ${error}`);
    return { success: false, syncedCount, errors };
  }
}

/**
 * Perform a complete sync operation
 */
export async function performSync(): Promise<void> {
  if (!isOnline) {
    console.log('Sync skipped: offline');
    return;
  }

  try {
    // Sync tasks
    await syncTasks();
    
    // Then sync media files
    await syncPhotos();
    await syncAudioRecordings();
    
    console.log('Sync completed successfully');
  } catch (error) {
    console.warn('Sync failed (this is normal if backend is not available):', error);
    // Don't spam console with errors, just log as warning
  }
}

/**
 * Sync tasks to backend
 */
async function syncTasks(): Promise<{ syncedCount: number; errors: string[] }> {
  const unsyncedTasks = await TasksDB.getUnsynced();
  const errors: string[] = [];
  let syncedCount = 0;

  for (const task of unsyncedTasks) {
    try {
      await TasksDB.update(task.id, { syncStatus: SyncStatus.SYNCING });

      // Fetch associated photo and audio metadata
      const photoMetadata = await db.getPhotosByTaskId(task.id);
      const audioMetadata = await db.getAudioByTaskId(task.id);

      // Construct the full task payload
      const taskPayload: Task = {
        ...task,
        photos: photoMetadata.length > 0 ? photoMetadata : undefined,
        audioRecordings: audioMetadata.length > 0 ? audioMetadata : undefined,
      };

      // Try to sync with API by sending the full Task object
      const result = await TasksAPI.create(taskPayload);

      if (result.success && result.task) {
        // Update local task with server data (which might include server-generated IDs, etc.)
        // The backend might return the fully merged/updated task object.
        await TasksDB.update(task.id, {
          ...result.task, // Use the task data returned by the backend
          syncStatus: SyncStatus.SYNCED,
          syncedAt: new Date().toISOString(),
          localId: task.localId, // Preserve localId if backend doesn't send it back or if it's different
        });
        syncedCount++;
      } else {
        await TasksDB.update(task.id, {
          syncStatus: SyncStatus.ERROR,
          lastError: result.error || 'Unknown API error during task sync',
          retryCount: (task.retryCount || 0) + 1,
        });
        errors.push(`Task ${task.id} (${task.title}): ${result.error}`);
      }
    } catch (error) {
      console.error(`Error syncing task ${task.id}:`, error);
      await TasksDB.update(task.id, {
        syncStatus: SyncStatus.ERROR,
        lastError: error instanceof Error ? error.message : String(error),
        retryCount: (task.retryCount || 0) + 1,
      });
      errors.push(`Task ${task.id} (${task.title}): ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  return { syncedCount, errors };
}

/**
 * Sync photos to S3
 */
async function syncPhotos(): Promise<void> {
  try {
    const unsyncedPhotos = await PhotosDB.getUnsynced();
    console.log(`Syncing ${unsyncedPhotos.length} photos`);
    
    for (const photo of unsyncedPhotos) {
      try {
        if (!photo.localBlob) {
          console.warn(`Photo ${photo.id} has no localBlob, skipping`);
          await PhotosDB.update(photo.id, { 
            syncStatus: SyncStatus.ERROR,
          });
          continue;
        }

        // Update status to syncing
        await PhotosDB.update(photo.id, { 
          syncStatus: SyncStatus.SYNCING,
        });

        // Create file from blob for upload
        const fileToUpload = new File([photo.localBlob], photo.filename, {
          type: photo.mimeType
        });
        
        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('taskId', photo.taskId);
        formData.append('type', 'photo');
        formData.append('photoId', photo.id);

        // Upload to S3 via our media upload API
        const response = await makeRequest<{ s3Key: string; s3Url: string; uploadedAt: string }>(
          API_ROUTES.MEDIA.UPLOAD,
          {
            method: 'POST',
            body: formData,
            headers: {}, // Don't set Content-Type for FormData - browser will set it with boundary
          }
        );

        if (response.success && response.data) {
          await PhotosDB.update(photo.id, { 
            syncStatus: SyncStatus.SYNCED, 
            s3Key: response.data.s3Key,
            s3Url: response.data.s3Url,
            uploadedAt: response.data.uploadedAt
          });
          console.log(`Photo ${photo.id} synced successfully to S3: ${response.data.s3Key}`);
        } else {
          await PhotosDB.update(photo.id, { 
            syncStatus: SyncStatus.ERROR,
          });
          console.error(`Failed to upload photo ${photo.id}:`, response.error);
        }
      } catch (error) {
        console.error(`Failed to sync photo ${photo.id}:`, error);
        await PhotosDB.update(photo.id, { 
          syncStatus: SyncStatus.ERROR,
        });
      }
    }
  } catch (error) {
    console.error('Error syncing photos:', error);
  }
}

/**
 * Sync audio recordings to S3
 */
async function syncAudioRecordings(): Promise<void> {
  try {
    const unsyncedAudio = await AudioRecordingsDB.getUnsynced();
    console.log(`Syncing ${unsyncedAudio.length} audio recordings`);
    
    for (const audio of unsyncedAudio) {
      try {
        if (!audio.localBlob) {
          console.warn(`Audio ${audio.id} has no localBlob, skipping`);
          await AudioRecordingsDB.update(audio.id, { 
            syncStatus: SyncStatus.ERROR,
          });
          continue;
        }

        // Update status to syncing
        await AudioRecordingsDB.update(audio.id, { 
          syncStatus: SyncStatus.SYNCING,
        });

        // Create file from blob for upload
        const fileToUpload = new File([audio.localBlob], audio.filename, {
          type: audio.mimeType
        });
        
        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('taskId', audio.taskId);
        formData.append('type', 'audio');
        formData.append('audioId', audio.id);

        // Upload to S3 via our media upload API
        const response = await makeRequest<{ s3Key: string; s3Url: string; uploadedAt: string }>(
          API_ROUTES.MEDIA.UPLOAD,
          {
            method: 'POST',
            body: formData,
            headers: {}, // Don't set Content-Type for FormData - browser will set it with boundary
          }
        );

        if (response.success && response.data) {
          await AudioRecordingsDB.update(audio.id, { 
            syncStatus: SyncStatus.SYNCED, 
            s3Key: response.data.s3Key,
            s3Url: response.data.s3Url,
            uploadedAt: response.data.uploadedAt
          });
          console.log(`Audio ${audio.id} synced successfully to S3: ${response.data.s3Key}`);
        } else {
          await AudioRecordingsDB.update(audio.id, { 
            syncStatus: SyncStatus.ERROR,
          });
          console.error(`Failed to upload audio ${audio.id}:`, response.error);
        }
      } catch (error) {
        console.error(`Failed to sync audio ${audio.id}:`, error);
        await AudioRecordingsDB.update(audio.id, { 
          syncStatus: SyncStatus.ERROR,
        });
      }
    }
  } catch (error) {
    console.error('Error syncing audio recordings:', error);
  }
}

/**
 * Force sync now
 */
export async function forceSyncNow(): Promise<void> {
  if (!navigator.onLine) {
    throw new Error('Cannot sync while offline');
  }
  
  await syncData();
}

/**
 * Get sync status
 */
export async function getSyncStatus(): Promise<{
  isOnline: boolean;
  pendingTasks: number;
  pendingPhotos: number;
  pendingAudio: number;
}> {
  const [tasks, photos, audio] = await Promise.all([
    TasksDB.getUnsynced(),
    PhotosDB.getUnsynced(),
    AudioRecordingsDB.getUnsynced(),
  ]);

  return {
    isOnline,
    pendingTasks: tasks.length,
    pendingPhotos: photos.length,
    pendingAudio: audio.length,
  };
}

/**
 * Clear sync queue
 */
export async function clearSyncQueue(): Promise<void> {
  await SyncQueueDB.clear();
}

/**
 * Cleanup old sync errors
 */
export async function cleanupSyncErrors(): Promise<void> {
  const tasks = await TasksDB.getAll();
  
  for (const task of tasks) {
    if (task.syncStatus === SyncStatus.ERROR && (task.retryCount || 0) >= SYNC_CONFIG.RETRY_ATTEMPTS) {
      // Reset error status for old failed items
      await TasksDB.update(task.id, {
        syncStatus: SyncStatus.PENDING,
        retryCount: 0,
        lastError: undefined,
      });
    }
  }
}

/**
 * Adds a task to the sync queue.
 * This is a simplified version for tasks only. 
 * A more robust solution would handle different resource types.
 */
async function queueTaskForSync(task: Task): Promise<number> {
  if (!task.localId) {
    console.error('Task has no localId, cannot queue for sync');
    throw new Error('Task requires a localId to be queued.');
  }
  return db.addToSyncQueue({
    type: 'create', // Or 'update' depending on context, simplified for now
    resourceType: 'task',
    resourceId: task.localId,
    data: task, // Store the whole task for now
    createdAt: new Date().toISOString(),
    retryCount: 0,
  });
}

/**
 * Main sync service export
 */
export const syncService = {
  initialize: initializeSync,
  syncNow: syncData,
  forceSync: forceSyncNow,
  getStatus: getSyncStatus,
  clearQueue: clearSyncQueue,
  cleanupErrors: cleanupSyncErrors,
  startPeriodic: startPeriodicSync,
  performSync,
  isOnline: () => isOnline,
  queueTask: queueTaskForSync,
}; 